import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";
import { FieldError, errCls } from "@/components/FieldError";

// Estrae in modo uniforme l'indirizzo strutturato da un cliente salvato,
// gestendo sia le chiavi EN (address/city/province) sia quelle IT (indirizzo/citta/provincia).
export const customerAddress = (c) => ({
  address: c?.address || c?.indirizzo || "",
  cap: c?.cap || "",
  city: c?.city || c?.citta || "",
  province: c?.province || c?.provincia || "",
});

// Campi indirizzo riutilizzabili: CAP con autocompletamento città/provincia (via /geo/postal)
// e dropdown per i CAP con più località. `onChange` riceve un patch { address?, cap?, city?, province? }.
export function AddressFields({
  values = {},
  onChange,
  prefix = "addr",
  disabled = false,
  showAddress = true,
  addressLabel = "Indirizzo",
  required = false,
  locked = {},
  errors = {},
}) {
  const lk = (k) => locked[k] ? <Lock className="inline w-3 h-3 ml-1 text-amber-500" data-testid={`${prefix}-${k}-lock`} /> : null;
  const [capMsg, setCapMsg] = useState("");
  const [capOptions, setCapOptions] = useState([]);
  const [picked, setPicked] = useState(-1);
  const star = required ? " *" : "";

  const onCapChange = async (e) => {
    const v = (e.target.value || "").replace(/\D/g, "").slice(0, 5);
    onChange({ cap: v });
    setCapMsg(""); setCapOptions([]); setPicked(-1);
    if (v.length === 5) {
      try {
        const { data } = await api.get(`/geo/postal?country=IT&zip=${v}`);
        const places = data?.places || [];
        if (places.length === 1) {
          const p = places[0];
          onChange({ cap: v, city: p.city || "", province: p.province || "" });
          setCapMsg(`Località: ${p.city}${p.province ? ` (${p.province})` : ""}`);
        } else if (places.length > 1) {
          onChange({ cap: v, city: places[0].city || "", province: places[0].province || "" });
          setCapOptions(places); setPicked(0);
          setCapMsg(`${places.length} località per questo CAP: tocca quella giusta qui sotto.`);
        } else { setCapMsg("CAP non trovato: inserisci città e provincia a mano."); }
      } catch { setCapMsg(""); }
    } else if (v.length > 0) { setCapMsg("Il CAP deve avere 5 cifre."); }
  };

  const pickLocality = (idx) => {
    const p = capOptions[idx]; if (!p) return;
    setPicked(idx);
    onChange({ city: p.city || "", province: p.province || "" });
    setCapMsg(`Località: ${p.city}${p.province ? ` (${p.province})` : ""}`);
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>CAP{star}{lk("cap")}</Label><Input disabled={disabled || !!locked.cap} className={`mt-1.5 border-primary/60 font-semibold ${errCls(errors.cap)}`} aria-invalid={!!errors.cap} inputMode="numeric" data-testid={`${prefix}-cap`} maxLength={5} value={values.cap || ""} onChange={onCapChange} placeholder="Inizia dal CAP" />{capMsg && capOptions.length < 2 && <p className="text-xs text-muted-foreground mt-1" data-testid={`${prefix}-cap-msg`}>{capMsg}</p>}<FieldError msg={errors.cap} testid={`${prefix}-cap-error`} /></div>
        <div><Label>Città{star}{lk("city")}</Label><Input disabled={disabled || !!locked.city} className={`mt-1.5 ${errCls(errors.city)}`} aria-invalid={!!errors.city} data-testid={`${prefix}-city`} value={values.city || ""} onChange={(e) => onChange({ city: e.target.value })} /><FieldError msg={errors.city} testid={`${prefix}-city-error`} /></div>
        <div><Label>Provincia{star}{lk("province")}</Label><Input disabled={disabled || !!locked.province} className={`mt-1.5 ${errCls(errors.province)}`} aria-invalid={!!errors.province} data-testid={`${prefix}-province`} maxLength={2} value={values.province || ""} onChange={(e) => onChange({ province: e.target.value.toUpperCase() })} placeholder="MI" /><FieldError msg={errors.province} testid={`${prefix}-province-error`} /></div>
      </div>
      {capOptions.length > 1 && (
        <div className="lg:col-span-2 flex flex-wrap items-center gap-1.5" data-testid={`${prefix}-cap-options`}><span className="text-xs text-muted-foreground mr-1">Località per questo CAP:</span>
          {capOptions.map((p, i) => <button key={i} type="button" onClick={() => pickLocality(i)}
            className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${picked === i ? "border-primary bg-background text-foreground font-semibold" : "border-border bg-background text-muted-foreground hover:border-primary"}`} data-testid={`${prefix}-cap-option-${i}`}>{p.city}{p.province ? ` (${p.province})` : ""}</button>)}
        </div>
      )}
      {showAddress && (
        <div><Label>{addressLabel}{star}{lk("address")}</Label><Input disabled={disabled || !!locked.address} className={`mt-1.5 ${errCls(errors.address)}`} aria-invalid={!!errors.address} data-testid={`${prefix}-address`} value={values.address || ""} onChange={(e) => onChange({ address: e.target.value })} /><FieldError msg={errors.address} testid={`${prefix}-address-error`} /></div>
      )}
    </>
  );
}
